import React, { useEffect, useRef, useState } from "react";
import GoogleMaps from "../GoogleMaps";
import { Form, Input } from "antd";
import Modal from "../Modal";
import { HiMapPin } from "react-icons/hi2";
import { LuLocateFixed } from "react-icons/lu";
import userLocationRetrieve from "../../config/utils/UserLocationRetrieve";
import ReverseGeocoding from "../../config/utils/ReverseGeoCoding";
import Button from "../FormElements/Button";

const LocationPicker = (props) => {
  const { onChange } = props;
  const [modal, setModal] = useState(props?.modal || false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationPreview, setSelectedLocationPreview] = useState("");
  const [selectedCoordinatesPreview, setSelectedCoordinatesPreview] =
    useState("");
  const [selectedCoordinates, setSelectedCoordinates] = useState("");
  const [hasSubmittedLocation, setHasSubmittedLocation] = useState(false);
  // especially last 2 states for business info module
  const [valueUpdateCheck, setValueUpdateCheck] = useState(true);
  const [editValue, setEditValue] = useState(props.value);
  const [editData, setEditData] = useState(props?.onEdit?.());

  const openModal = () => {
    setModal(true);
  };

  const getCurrentLocation = async () => {
    const coords = await userLocationRetrieve();
    if (coords?.latitude) {
      const location = await ReverseGeocoding({
        lat: coords?.latitude,
        lng: coords?.longitude,
      });
      setSelectedLocationPreview(location?.formatted_address);
      setSelectedCoordinatesPreview({
        lat: coords?.latitude,
        lng: coords?.longitude,
      });
      if (onChange) {
        onChange({
          lat: coords?.latitude,
          lng: coords?.longitude,
          address: location?.formatted_address,
          postalCode: location?.postalCode,
          town: location?.town,
          city: location?.city,
          country: location?.country,
        });
      }
    }
  };

  const handleUpdatedPositions = async (coords) => {
    if (coords && coords.length) {
      coords = coords[0];
      const location = await ReverseGeocoding(coords);
      setSelectedLocationPreview(location?.formatted_address);
      setSelectedCoordinatesPreview(coords);
      // if(!location.address || !location?.country || !location?.city){
      //     dispatch(setError('Please select valid location'))
      //     return
      // }
      if (onChange) {
        onChange({
          ...coords,
          address: location?.formatted_address,
          postalCode: location?.postalCode,
          town: location?.town,
          city: location?.city,
          country: location?.country,
        });
      }
    }
  };

  const closeModal = () => {
    setSelectedLocationPreview("");
    setSelectedCoordinatesPreview("");
    setModal(false);
    if (props?.isBusinessInfo && !valueUpdateCheck) {
      setSelectedLocationPreview(editValue);
      setHasSubmittedLocation(true);
    }
  };

  const handlePlaceAutoComplete = (searchParams) => {
    const { formatted_address, latitude, longitude } = searchParams;
    setSelectedLocationPreview(formatted_address);
    setSelectedCoordinatesPreview({ lat: latitude, lng: longitude });
    if (onChange) {
      onChange({
        lat: latitude,
        lng: longitude,
        address: formatted_address,
      });
    }
  };

  // Handles manually typed address — uses current map/GPS coordinates if available,
  // otherwise forward-geocodes the typed text to get lat/lng (works even with location off)
  const handleManualAddressEntry = async (address) => {
    setSelectedLocationPreview(address);

    // Use existing coordinates if already set (from GPS or map drag)
    let coords =
      selectedCoordinatesPreview && selectedCoordinatesPreview.lat
        ? {
            lat: selectedCoordinatesPreview.lat,
            lng: selectedCoordinatesPreview.lng,
          }
        : null;

    // No coordinates yet — try to geocode the typed address using the already-loaded Google API
    if (!coords && window.google?.maps?.Geocoder) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results[0]) {
              resolve(results[0]);
            } else {
              reject(status);
            }
          });
        });
        coords = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        };
        // Also update the map preview so the pin moves to the geocoded position
        setSelectedCoordinatesPreview(coords);
      } catch (e) {
        console.log("Geocoding failed for manual address:", e);
      }
    }

    if (onChange) {
      onChange({
        address,
        ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
      });
    }
  };

  const submitLocationData = () => {
    closeModal();
    setSelectedLocation(selectedLocationPreview);
    setSelectedCoordinates(selectedCoordinatesPreview);
    setHasSubmittedLocation(true);
    // especially for business info module
    props?.addressPicker &&
      props?.addressPicker(selectedCoordinatesPreview, selectedLocationPreview);
  };

  const autoCompleteValueEmptyHandler = () => {
    console.log("autoCompleteValueEmptyHandler");
  };
  useEffect(() => {
    if (modal) {
      setValueUpdateCheck(false);
      if (selectedLocation) {
        setSelectedLocationPreview(selectedLocation);
        setSelectedCoordinatesPreview(selectedCoordinates);
      } else {
        getCurrentLocation();
      }
    } else {
      //to close stand alone component
      props?.onClose?.();
    }
  }, [modal]);

  // if we have a onEdit case we are setting the intital value for location.
  useEffect(() => {
    if (props?.onEdit?.()) {
      let editLocationData = props?.onEdit?.();
      setSelectedLocationPreview(editData || editLocationData);
      setSelectedLocation(editData || editLocationData);
      setHasSubmittedLocation(true);
      if (props?.coordinates) {
        setSelectedCoordinates({
          lat: props?.coordinates?.latitude,
          lng: props?.coordinates?.longitude,
        });
        setSelectedCoordinatesPreview({
          lat: props?.coordinates?.latitude,
          lng: props?.coordinates?.longitude,
        });
      }
    }
  }, [editData, props?.coordinates]);

  // especially for business info module
  useEffect(() => {
    if (props?.value && props?.isBusinessInfo && valueUpdateCheck) {
      setHasSubmittedLocation(true);
      setSelectedLocation(props?.value);
      setSelectedLocationPreview(props?.value);
      if (props?.coordinates) {
        let coordinates = {
          lat: props?.coordinates?.businessLatitude,
          lng: props?.coordinates?.businessLongitude,
        };
        setSelectedCoordinates(coordinates);
      }
    }
    if (props?.customFieldReset) {
      setSelectedLocationPreview("");
      setSelectedLocation("");
      setHasSubmittedLocation(false);
      props.setCustomFieldReset({ ...props.customFieldReset, address: false });
    }
  }, [props?.value, props?.coordinates, props?.customFieldReset]);

  return (
    <div className="flex cursor-pointer">
      {!props?.hideInput ? (
        <Input
          className="rounded-full h-10 location-box"
          style={{ minHeight: "45px" }} // Add cursor style directly here
          placeholder="your location"
          value={hasSubmittedLocation ? selectedLocation : ""}
          onClick={openModal}
          disabled={props?.disabled ?? false}
          type="text"
          readOnly
          suffix={
            <React.Fragment>
              <span>
                <HiMapPin
                  onClick={openModal}
                  className="text-slate-400 text-primary text-2xl"
                />
              </span>
            </React.Fragment>
          }
        />
      ) : (
        ""
      )}

      <Modal.Basic
        open={modal}
        onCancel={closeModal}
        width={500}
        maskClosable={false}
      >
        <div className="flex w-full items-center justify-between p-4 pb-0">
          <div className="flex flex-col w-full items-start">
            <h5 className="heading-5 font-bold mr-2">Location:</h5>
            <Input
              className="rounded-full h-10 location-box"
              placeholder="Your location"
              value={selectedLocationPreview}
              type="text"
              disabled
              suffix={
                <React.Fragment>
                  <span>
                    <LuLocateFixed
                      className="text-slate-400 text-primary text-2xl cursor-pointer"
                      onClick={getCurrentLocation}
                    />
                  </span>
                </React.Fragment>
              }
            />
          </div>
        </div>
        <div className="w-full h-full p-4">
          <GoogleMaps
            showAutoComplete
            isDraggable
            zoom={20}
            pointerPositions={[selectedCoordinatesPreview]}
            handleUpdatedPositions={handleUpdatedPositions}
            handlePlaceAutoComplete={handlePlaceAutoComplete}
            autoCompleteValueEmptyHandler={autoCompleteValueEmptyHandler}
            onManualAddressEntry={handleManualAddressEntry}
          />
          <div className="absolute bottom-[20px] left-[20px] shadow-2xl">
            <Button.Basic
              type="primary"
              size="large"
              text="Submit"
              className="bg-primary"
              onClick={submitLocationData}
              disabled={false}
            />
          </div>
        </div>
      </Modal.Basic>
    </div>
  );
};

export default LocationPicker;
